import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import {
    useGetEquipmentByIdQuery,
    useCreateEquipmentMutation,
    useEditEquipmentMutation,
    useGetCategoriesQuery,
    useGetSubscribersQuery
} from "../../../services";
import { CategoryType, IEquipment, IStatus, AccessControl } from "../../../types";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";

const Equipment: React.FC = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { equipmentid } = useParams();

    const {
        data: equipmentResponse,
        isLoading: isEquipmentLoading,
        isError: isEquipmentError,
    } = useGetEquipmentByIdQuery(equipmentid as string, {
        skip: !equipmentid,
    });

    const {
        data: categoriesResponse,
        isLoading,
        isFetching,
        error,
    } = useGetCategoriesQuery();
    const { data: subscribersResponse } = useGetSubscribersQuery();

    const [createEquipment, { isLoading: isCreating }] = useCreateEquipmentMutation();
    const [editEquipment, { isLoading: isUpdating }] = useEditEquipmentMutation();

    const isSaving = isCreating || isUpdating;

    const [formData, setFormData] = useState<Partial<IEquipment>>({
        name: "",
        description: "",
        category: "",
        access: AccessControl.GLOBAL,
        subscriber: "",
        organisation: "",
        status: "Active" as IStatus,
    });

    const [errors, setErrors] = useState<Partial<Record<keyof IEquipment, string>>>({});

    // Hydrate form when equipment detail loads
    useEffect(() => {
        if (!equipmentResponse?.result) return;
        const e = equipmentResponse.result;
        setFormData({
            name: e.name ?? "",
            description: e.description ?? "",
            category: typeof e.category === "object" ? e.category?._id : e.category ?? "",
            access: e.access ?? AccessControl.GLOBAL,
            subscriber: typeof e.subscriber === "object" ? e.subscriber?._id : e.subscriber ?? "",
            organisation: typeof e.organisation === "object" ? e.organisation?._id : e.organisation ?? "",
            status: e.status ?? "Active",
        });
    }, [equipmentResponse]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name as keyof IEquipment])
            setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof IEquipment, string>> = {};
        if (!formData.name?.trim()) newErrors.name = "Name is required";
        if (!formData.category) newErrors.category = "Category is required";


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (equipmentid) {
                await editEquipment({
                    id: equipmentid,
                    data: formData,
                }).unwrap();
            } else {
                await createEquipment(formData).unwrap();
            }
            navigate(adminRoutes.equipmentList);
        } catch (err) {
            console.error("Save equipment failed:", err);
        }
    };

    // Sidebar compensation
    const isSidebarOpen = useAppSelector(
        (state: RootState) => state.navbar.isOpen
    );
    const sidebarWidth = isSidebarOpen ? 256 : 64;

    if (equipmentid && isEquipmentLoading) {
        return (
            <div className={`pt-20 text-center ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Loading equipment...
            </div>
        );
    }

    return (
        <div
            className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-black" : "bg-gray-50"
                }`}
            style={{
                marginLeft: sidebarWidth,
                width: `calc(100% - ${sidebarWidth}px)`,
            }}
        >
            {/* Header */}
            <div className="mb-6">
                <h1
                    className={`text-2xl font-semibold ${theme === "dark" ? "text-gray-300" : "text-gray-800"
                        }`}
                >
                    {equipmentid ? "Edit Equipment" : "Create Equipment"}
                </h1>
                <div className="flex items-center gap-2 mt-2 text-sm">
                    <Link
                        to={adminRoutes.equipmentList}
                        className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
                    >
                        All Equipment
                    </Link>
                    <span className={theme === "dark" ? "text-gray-400" : "text-gray-600"}>/</span>
                    <span className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}>
                        {equipmentid ? "Edit Equipment" : "Create Equipment"}
                    </span>
                </div>
            </div>

            <div
                className={`${theme === "dark"
                    ? "bg-[#1E1E1E] border border-gray-800"
                    : "bg-white border border-gray-200"
                    } rounded-lg p-6`}
            >
                <div className="mb-6">
                    <h2
                        className={`text-base font-medium border-b pb-2 border-gray-700 ${theme === "dark" ? "text-white" : "text-gray-800"
                            }`}
                    >
                        Equipment Information
                    </h2>
                </div>

                {isEquipmentError && (
                    <p className="text-red-500 mb-4">Failed to load equipment details.</p>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Name */}
                        <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Equipment Name
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter equipment name"
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.name
                                    ? "border border-red-500"
                                    : theme === "dark"
                                        ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                                        : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                        </div>

                        {/* Category */}
                        <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category as string}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.category
                                    ? "border border-red-500"
                                    : theme === "dark"
                                        ? "bg-black border border-gray-800 text-white"
                                        : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="" disabled>Select Category</option>
                                {categoriesResponse?.map((cat) => (
                                    <option key={cat._id} value={cat._id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                            {errors.category && (
                                <p className="text-red-500 text-xs mt-1">{errors.category}</p>
                            )}
                        </div>
                         {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Subscriber
                            </label>
                            <select
                                name="subscriber"
                                value={formData.subscriber as string}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white"
                                    : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="" disabled>Select Subscriber</option>
                                {subscribersResponse?.map((sub: any) => (
                                    <option key={sub._id} value={sub._id}>
                                        {sub.name}
                                    </option>
                                ))}
                            </select>
                        </div> */}

                        {/* Access */}
                        {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Access
                            </label>
                            <select
                                name="access"
                                value={formData.access}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white"
                                    : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
 <option value="" disabled>Select Access</option>

                                {Object.values(AccessControl).map((access) => (
                                    <option key={access} value={access}>
                                        {access}
                                    </option>
                                ))}
                            </select>
                        </div> */}

                        {/* Status */}
                        {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${errors.status
                                    ? "border border-red-500"
                                    : theme === "dark"
                                        ? "bg-black border border-gray-800 text-white"
                                        : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                            {errors.status && (
                                <p className="text-red-500 text-xs mt-1">{errors.status}</p>
                            )}
                        </div> */}

                        {/* Organisation */}
                        {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Organisation
                            </label>
                            <select
                                name="organisation"
                                value={formData.organisation as string}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white"
                                    : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="">Select Organisation</option>
                                {organisationsResponse?.map((org: any) => (
                                    <option key={org._id} value={org._id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                        </div> */}

                        {/* Subscriber */}
                       

                        {/* Description */}
                        <div className="md:col-span-2">
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter equipment description"
                                rows={4}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-8 py-2 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                            style={{
                                background:
                                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                            }}
                        >
                            {isSaving ? "Saving..." : equipmentid ? "Update" : "Create"}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(adminRoutes.equipmentList)}
                            className={`px-8 py-2 rounded-md font-medium ${theme === "dark"
                                ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                                }`}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Equipment;
